import { Component, computed, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Device } from '../../../../../Models/avyna/device.model';
import {
  DeviceCertificate,
  DiskEncryption,
  MicrosoftDefender,
  MicrosoftPatch,
} from '../../../../../Models/avyna/agent-device.model';
import { AvCardComponent } from '../../../../Shared/Components/av-card/av-card.component';
import { AvContentContainerComponent } from '../../../../Shared/Components/av-content-container/av-content-container.component';
import { AvTableCard } from '../../../../Shared/Components/av-table-card/av-table-card.component';
import { COMMON_IMPORTS } from '../../../../Shared/common-imports';
import { AvCardDialogConfig, AvCardField } from '../../../../Shared/Interfaces/av-card.interface';
import type { DialogEntry } from '../../../../Shared/Interfaces/dialog.interface';
import {
  AvTableColumn,
  AvTableDialogConfig,
  AvTableLinkCell,
  AvTableRow,
} from '../../../../Shared/Interfaces/av-table-card.interface';
import { ShowDeviceFacade } from '../../../../../Facades/Intune/show-device.facade';

@Component({
  selector: 'app-security',
  standalone: true,
  imports: [COMMON_IMPORTS, AvCardComponent, AvContentContainerComponent, AvTableCard],
  templateUrl: './security.component.html',
  styleUrls: ['./security.component.scss'],
})
export class SecurityComponent {
  private readonly _facade = inject(ShowDeviceFacade);
  private readonly translate = inject(TranslateService);

  readonly device = this._facade.device;

  readonly patchColumns: AvTableColumn[] = [
    { key: 'kb', label: 'app.device.security.patches.columns.kb', sortable: true },
    { key: 'description', label: 'app.device.security.patches.columns.description', sortable: true },
    { key: 'installDate', label: 'app.device.security.patches.columns.install_date', sortable: true },
  ];

  readonly certificateColumns: AvTableColumn[] = [
    { key: 'issuer', label: 'app.device.security.certificates.columns.issuer', sortable: true },
    { key: 'connect', label: 'app.device.security.certificates.columns.connect', sortable: true },
    { key: 'version', label: 'app.device.security.certificates.columns.version', sortable: true },
    { key: 'details', label: 'app.device.security.certificates.columns.details', type: 'link' },
  ];

  readonly diskEncryptionColumns: AvTableColumn[] = [
    { key: 'drive', label: 'app.device.security.disk_encryption.columns.drive', sortable: true },
    { key: 'status', label: 'app.device.security.disk_encryption.columns.status', sortable: true },
    {
      key: 'recoveryKeyAvailable',
      label: 'app.device.security.disk_encryption.columns.recovery_key_available',
      type: 'bool',
      sortable: true,
    },
    { key: 'details', label: 'app.device.security.disk_encryption.columns.details', type: 'link' },
  ];

  readonly patchRows = computed<AvTableRow[]>(() => {
    const device = this.device();
    const patches = device?.agent?.security?.microsoft_patches;
    if (!Array.isArray(patches)) return [];

    return patches.map((patch: MicrosoftPatch) => ({
      kb: this.fallbackString(patch?.kb),
      description: this.fallbackString(patch?.description),
      installDate: this.fallbackString(patch?.install_date),
    }));
  });

  readonly certificateRows = computed<AvTableRow[]>(() => {
    const device = this.device();
    const certificates = device?.agent?.security?.certificates;
    if (!Array.isArray(certificates)) return [];

    return certificates.map((certificate: DeviceCertificate) => ({
      issuer: this.fallbackString(certificate?.issuer),
      connect: this.formatCertificateConnect(certificate?.connect),
      version: this.fallbackString(certificate?.version),
      context: this.fallbackString(certificate?.context),
      details: this.detailsLink(this.buildCertificateDialog(certificate)),
    }));
  });

  readonly diskEncryptionRows = computed<AvTableRow[]>(() => {
    const device = this.device();
    const items = device?.agent?.security?.disk_encryption;
    if (!Array.isArray(items)) return [];

    return items.map((item: DiskEncryption) => ({
      drive: this.fallbackString(item?.drive),
      status: this.fallbackString(item?.status),
      recoveryKeyAvailable: this.asBool(item?.recovery_key_available),
      details: this.detailsLink(this.buildDiskEncryptionDialog(item)),
    }));
  });

  readonly defenderFields = computed<AvCardField[]>(() => {
    const defender: MicrosoftDefender | undefined = this.device()?.agent?.security?.microsoft_defender;
    const entries: { label: string; value: string | number | boolean | null | undefined }[] = [
      { label: 'app.device.security.defender.fields.status', value: defender?.status },
      { label: 'app.device.security.defender.fields.antivirus_enabled', value: defender?.antivirus_enabled },
      { label: 'app.device.security.defender.fields.antispyware_enabled', value: defender?.antispyware_enabled },
      { label: 'app.device.security.defender.fields.real_time_protection_enabled', value: defender?.real_time_protection_enabled },
    ];

    return entries.map((entry) => ({
      label: entry.label,
      value: this.asCardValue(entry.value),
    }));
  });

  readonly defenderDialog = computed<AvCardDialogConfig | null>(() => {
    const defender: MicrosoftDefender | undefined = this.device()?.agent?.security?.microsoft_defender;
    if (!defender) return null;
    return {
      titleKey: 'app.device.security.dialog.defender_details.title',
      entries: this.buildDefenderEntries(defender),
    };
  });

  private detailsLink(dialog: AvTableDialogConfig): AvTableLinkCell {
    return {
      icon: 'more_horiz',
      ariaLabel: 'app.device.security.columns.details',
      dialog,
    };
  }

  private buildCertificateDialog(certificate: DeviceCertificate): AvTableDialogConfig {
    return {
      titleKey: 'app.device.security.dialog.certificate_details.title',
      entries: [
        { labelKey: 'app.device.security.certificates.details.issuer', value: certificate?.issuer },
        { labelKey: 'app.device.security.certificates.details.connect', value: this.formatCertificateConnect(certificate?.connect) },
        { labelKey: 'app.device.security.certificates.details.context', value: certificate?.context },
        { labelKey: 'app.device.security.certificates.details.subject', value: certificate?.subject },
        { labelKey: 'app.device.security.certificates.details.version', value: certificate?.version },
        { labelKey: 'app.device.security.certificates.details.thumbprint', value: certificate?.thumbprint },
        { labelKey: 'app.device.security.certificates.details.valid_from', value: certificate?.valid_from },
        { labelKey: 'app.device.security.certificates.details.valid_until', value: certificate?.valid_until },
        { labelKey: 'app.device.security.certificates.details.serial_number', value: certificate?.serial_number },
      ],
    };
  }

  private buildDefenderEntries(defender: MicrosoftDefender | undefined): DialogEntry[] {
    return [
      { labelKey: 'app.device.security.defender.fields.status', value: defender?.status },
      { labelKey: 'app.device.security.defender.fields.computer_id', value: defender?.computer_id },
      { labelKey: 'app.device.security.defender.fields.error_message', value: defender?.error_message },
      { labelKey: 'app.device.security.defender.fields.full_scan_age', value: defender?.full_scan_age },
      { labelKey: 'app.device.security.defender.fields.computer_state', value: defender?.computer_state },
      { labelKey: 'app.device.security.defender.fields.quick_scan_age', value: defender?.quick_scan_age },
      { labelKey: 'app.device.security.defender.fields.am_running_mode', value: defender?.am_running_mode },
      { labelKey: 'app.device.security.defender.fields.reboot_required', value: defender?.reboot_required },
      { labelKey: 'app.device.security.defender.fields.am_engine_version', value: defender?.am_engine_version },
      { labelKey: 'app.device.security.defender.fields.antivirus_enabled', value: defender?.antivirus_enabled },
      { labelKey: 'app.device.security.defender.fields.nas_signature_age', value: defender?.nas_signature_age },
      { labelKey: 'app.device.security.defender.fields.am_product_version', value: defender?.am_product_version },
      { labelKey: 'app.device.security.defender.fields.am_service_enabled', value: defender?.am_service_enabled },
      { labelKey: 'app.device.security.defender.fields.am_service_version', value: defender?.am_service_version },
      { labelKey: 'app.device.security.defender.fields.full_scan_end_time', value: defender?.full_scan_end_time },
      { labelKey: 'app.device.security.defender.fields.is_virtual_machine', value: defender?.is_virtual_machine },
      { labelKey: 'app.device.security.defender.fields.antispyware_enabled', value: defender?.antispyware_enabled },
      { labelKey: 'app.device.security.defender.fields.quick_scan_end_time', value: defender?.quick_scan_end_time },
      { labelKey: 'app.device.security.defender.fields.full_scan_start_time', value: defender?.full_scan_start_time },
      { labelKey: 'app.device.security.defender.fields.last_full_scan_source', value: defender?.last_full_scan_source },
      { labelKey: 'app.device.security.defender.fields.nas_signature_version', value: defender?.nas_signature_version },
      { labelKey: 'app.device.security.defender.fields.quick_scan_start_time', value: defender?.quick_scan_start_time },
      { labelKey: 'app.device.security.defender.fields.last_quick_scan_source', value: defender?.last_quick_scan_source },
      { labelKey: 'app.device.security.defender.fields.antivirus_signature_age', value: defender?.antivirus_signature_age },
      { labelKey: 'app.device.security.defender.fields.ioav_protection_enabled', value: defender?.ioav_protection_enabled },
      { labelKey: 'app.device.security.defender.fields.behavior_monitor_enabled', value: defender?.behavior_monitor_enabled },
      { labelKey: 'app.device.security.defender.fields.tamper_protection_source', value: defender?.tamper_protection_source },
      { labelKey: 'app.device.security.defender.fields.antispyware_signature_age', value: defender?.antispyware_signature_age },
      { labelKey: 'app.device.security.defender.fields.is_engine_update_available', value: defender?.is_engine_update_available },
      { labelKey: 'app.device.security.defender.fields.nas_signature_last_updated', value: defender?.nas_signature_last_updated },
      { labelKey: 'app.device.security.defender.fields.antivirus_signature_version', value: defender?.antivirus_signature_version },
      { labelKey: 'app.device.security.defender.fields.is_platform_update_available', value: defender?.is_platform_update_available },
      { labelKey: 'app.device.security.defender.fields.on_access_protection_enabled', value: defender?.on_access_protection_enabled },
      { labelKey: 'app.device.security.defender.fields.real_time_protection_enabled', value: defender?.real_time_protection_enabled },
      { labelKey: 'app.device.security.defender.fields.real_time_signature_delivery', value: defender?.real_time_signature_delivery },
      { labelKey: 'app.device.security.defender.fields.antispyware_signature_version', value: defender?.antispyware_signature_version },
      { labelKey: 'app.device.security.defender.fields.is_signature_update_available', value: defender?.is_signature_update_available },
      { labelKey: 'app.device.security.defender.fields.antivirus_signature_last_updated', value: defender?.antivirus_signature_last_updated },
      { labelKey: 'app.device.security.defender.fields.network_inspection_system_enabled', value: defender?.network_inspection_system_enabled },
      { labelKey: 'app.device.security.defender.fields.antispyware_signature_last_updated', value: defender?.antispyware_signature_last_updated },
    ];
  }

  private buildDiskEncryptionDialog(item: DiskEncryption): AvTableDialogConfig {
    return {
      titleKey: 'app.device.security.dialog.disk_encryption_details.title',
      entries: [
        { labelKey: 'app.device.security.disk_encryption.details.drive', value: item?.drive },
        { labelKey: 'app.device.security.disk_encryption.details.encrypted', value: item?.encrypted },
        { labelKey: 'app.device.security.disk_encryption.details.type', value: item?.type },
        { labelKey: 'app.device.security.disk_encryption.details.encryption_key_length', value: item?.encryption_key_length },
        { labelKey: 'app.device.security.disk_encryption.details.algorithm', value: item?.algorithm },
        { labelKey: 'app.device.security.disk_encryption.details.status', value: item?.status },
        { labelKey: 'app.device.security.disk_encryption.details.recovery_key_available', value: item?.recovery_key_available },
      ],
    };
  }

  private formatCertificateConnect(value?: string | null): string {
    if (value === 'Private Key Available') {
      return this.translate.instant('app.device.security.certificates.connect.private_key_available');
    }
    if (value === 'Public Key Only') {
      return this.translate.instant('app.device.security.certificates.connect.public_key_only');
    }
    return this.translate.instant('shared.general.labels.na');
  }

  private fallbackString(value?: string | null): string {
    if (value && value.trim().length > 0) return value;
    return this.translate.instant('shared.general.labels.na');
  }

  private asBool(value: unknown): boolean | null {
    return typeof value === 'boolean' ? value : null;
  }

  private asCardValue(value: string | number | boolean | null | undefined): string | boolean | null | undefined {
    if (typeof value === 'number') return String(value);
    return value;
  }

}
